import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Label } from '../../../../../components/ui/label';
import { PipeSelector } from '../../../../../components/ui/PipeSelector';
import { SpecificationDocument } from '../../../types/index';
import { DocumentGroups } from './DocumentGroups';
import { Trash2, FileText, Package } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import type { DashboardProject } from '../../../types/project';

interface AllProjectSpecificationsProps {
  project: DashboardProject;
  groupedSpecifications: Record<string, SpecificationDocument[]>;
  handleRefresh: (spec: SpecificationDocument | null) => void;
  handleDeleteDocument: (spec: SpecificationDocument) => void;
  handleDeletePipeSize: (specs: SpecificationDocument[], pipeName: string) => void;
  setupData: any;
  canEdit?: boolean;
  canReport?: boolean;
  fatTemplateListVersion?: number;
  toolbar?: React.ReactNode;
}

const TYPE_LABELS: Record<string, string> = {
  pipeline: 'Pipeline',
  fabrication: 'Fabrication',
  others: 'Others',
};

const SUBTYPE_LABELS: Record<string, string> = {
  buckleArrestor: 'Buckle Arrestor',
  pipeInPipe: 'Pipe in Pipe',
  bulkhead: 'Bulk Head',
  spools: 'Spools',
  none: '',
};

const formatGroupLabel = (projectType: string, subType: string): string => {
  const typeLabel = TYPE_LABELS[projectType] || projectType;
  if (projectType === 'fabrication' && subType && SUBTYPE_LABELS[subType]) {
    return SUBTYPE_LABELS[subType];
  }
  return typeLabel;
};

export const AllProjectSpecifications: React.FC<AllProjectSpecificationsProps> = ({
  project,
  groupedSpecifications,
  handleRefresh,
  handleDeleteDocument,
  handleDeletePipeSize,
  setupData,
  canEdit = true,
  canReport = true,
  fatTemplateListVersion = 0,
  toolbar,
}) => {
  const allPipes = useMemo(() => {
    const pipes: any[] = [];
    if (setupData && typeof setupData === 'object') {
      Object.values(setupData).forEach((setup: any) => {
        if (!setup || !setup.pipeSizes || !Array.isArray(setup.pipeSizes)) return;
        const projectType = setup.projectType || '';
        const projectSubType = setup.projectSubType || 'none';

        // Skip if this project type / subtype isn't actually enabled on the project
        if (projectType === 'pipeline' && !project.project.projectTypes.pipeline?.enabled) return;
        if (projectType === 'fabrication') {
          const fab = project.project.projectTypes.fabrication;
          if (!fab?.enabled) return;
          const opts: any = fab.options || {};
          if (!opts[projectSubType]?.selected) return;
        }
        if (projectType === 'others' && !project.project.projectTypes.others?.enabled) return;

        setup.pipeSizes.forEach((pipe: any, index: number) => {
          if (!pipe || !pipe.pipeName) return;
          const pipeOrder = pipe.pipeOrder || index + 1;
          pipes.push({
            pipeId: pipe.pipeId || pipe.pipeName,
            pipeName: pipe.pipeName,
            size: pipe.pipeSize || pipe.size || 0,
            wallThickness: pipe.wallThickness || 0,
            pipeOrder,
            projectType,
            projectSubType,
            spoolSource: pipe,
          });
        });
      });
    }
    return pipes;
  }, [setupData, project]);

  const [selectedPipeId, setSelectedPipeId] = useState<string | null>(null);

  useEffect(() => {
    if (allPipes.length === 0) {
      setSelectedPipeId(null);
      return;
    }
    const stillValid = selectedPipeId && allPipes.some((p) => p.pipeId?.toString() === selectedPipeId);
    if (!stillValid) {
      setSelectedPipeId(allPipes[0].pipeId?.toString() || null);
    }
  }, [allPipes, selectedPipeId]);

  if (allPipes.length === 0) {
    return (
      <div className="space-y-6 pt-4">
        {toolbar && <div className="flex items-center justify-end gap-2">{toolbar}</div>}
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 bg-page-bg rounded-full flex items-center justify-center mb-4">
            <Package className="h-8 w-8 text-heritage-grey" />
          </div>
          <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue mb-2">
            No pipes created yet
          </h3>
          <p className="text-[12px] text-heritage-grey max-w-sm">
            You need to create pipes in the Setup section first before adding specifications.
          </p>
        </div>
      </div>
    );
  }

  const selectedPipe = allPipes.find((p) => p.pipeId?.toString() === selectedPipeId);
  const selectedSpecs = selectedPipeId ? groupedSpecifications[selectedPipeId] || [] : [];
  const selectedGroupLabel = selectedPipe
    ? formatGroupLabel(selectedPipe.projectType, selectedPipe.projectSubType)
    : '';

  return (
    <div className="space-y-6 pt-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Label className="text-[12px] font-medium text-text-grey whitespace-nowrap">Pipe Size:</Label>
          <PipeSelector
            value={selectedPipeId}
            onChange={(v) => setSelectedPipeId(v)}
            placeholder="Select pipe size"
            options={allPipes.map((pipe) => {
              const groupLabel = formatGroupLabel(pipe.projectType, pipe.projectSubType);
              return {
                value: pipe.pipeId?.toString() || '',
                label: `${groupLabel} · Pipe ${pipe.pipeOrder} (${pipe.size || 0}" × ${pipe.wallThickness || 0}")`,
              };
            })}
          />
        </div>
        {toolbar && <div className="flex items-center gap-2 flex-shrink-0">{toolbar}</div>}
      </div>

      {selectedPipe && (
        <Card className="border border-[#E2E5EA]">
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-heritage-grey">
                  {selectedSpecs.length} document{selectedSpecs.length !== 1 ? 's' : ''}
                </span>
                {canEdit && selectedSpecs.length > 0 && (
                  <Button
                    onClick={() =>
                      handleDeletePipeSize(
                        selectedSpecs,
                        `${selectedGroupLabel} · Pipe ${selectedPipe.pipeOrder} (${selectedPipe.size || 0}" × ${selectedPipe.wallThickness || 0}")`
                      )
                    }
                    size="sm"
                    variant="outline"
                    className="ml-auto h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    title="Delete all specifications for this pipe"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {selectedSpecs.length > 0 ? (
                <DocumentGroups
                  specs={selectedSpecs}
                  onRefresh={handleRefresh}
                  handleDeleteDocument={handleDeleteDocument}
                  project={project}
                  canEdit={canEdit}
                  canReport={canReport}
                  fatTemplateListVersion={fatTemplateListVersion}
                  fatPipeContext={
                    selectedPipe.projectSubType === 'spools'
                      ? {
                          spoolSource: selectedPipe.spoolSource,
                          showSpoolFatChecklist: true,
                          showSpoolCoatingChecklist: true,
                        }
                      : undefined
                  }
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="w-12 h-12 bg-page-bg rounded-full flex items-center justify-center mb-3">
                    <FileText className="h-6 w-6 text-heritage-grey" />
                  </div>
                  <p className="font-heading text-[13px] font-semibold text-blueprint-blue mb-1">
                    No specifications yet
                  </p>
                  <p className="text-[12px] text-heritage-grey max-w-sm">
                    Click "Add Specification" above to add documents for this pipe.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
